import { AppDispatch, createAppThunk, RootState } from '@mse-reg/app/store';
import {
  AcademicYearDto,
  ISemesterSelectionClient,
  PhaseDto,
  SemesterInfoDto,
  SemesterSelectionFilter,
} from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export const getSemester = (state: { semesterFilter: SemesterSelectionFilter }, semesterId: number) => {
  return state.semesterFilter?.semesters?.find((s) => s.id === semesterId);
};
const getPhaseForSemester = (
  state: { semesterFilter: SemesterSelectionFilter; selectedPhase: PhaseDto },
  semester: SemesterInfoDto,
) => {
  if (state.selectedPhase?.semesterId === semester.id) {
    return state.selectedPhase;
  }

  if (!state.selectedPhase) {
    return semester?.phases?.find((p) => p.id === semester.currentPhaseId);
  }

  return null;
};
const getOpenSemesters = (filter: SemesterSelectionFilter) => {
  return filter?.semesters?.filter((s) => !s.isPast && s.registrationOpen).map((s) => ({ ...s })) ?? [];
};
const getAcademicYear = (state: { semesterFilter: SemesterSelectionFilter }, academicYearId?: number) => {
  if (academicYearId) {
    return state.semesterFilter?.academicYears?.find((a) => a.id === academicYearId);
  }
  return state.semesterFilter?.academicYears?.find((a) => a.id === state.semesterFilter?.currentAcademicYearId);
};
const getPhase = (state: { semesterFilter: SemesterSelectionFilter }, phaseId: number) => {
  const semester = state.semesterFilter?.semesters?.find((s) => s.phases?.filter((p) => p.id === phaseId).length > 0);
  return semester?.phases?.find((p) => p.id === phaseId);
};

const getSemesterForAcademicYear = (
  state: { semesterFilter: SemesterSelectionFilter; selectedSemester: SemesterInfoDto },
  academicYearId: number,
) => {
  if (state.selectedSemester?.academicYearId === academicYearId) {
    return state.selectedSemester;
  }

  const defaultSemester = getSemester(state, state.semesterFilter?.defaultSemesterId);
  if (defaultSemester?.academicYearId === academicYearId) {
    return defaultSemester;
  }

  return state.semesterFilter?.semesters?.filter((s) => s.academicYearId === academicYearId)[0];
};

export type SemesterFilterValues = {
  academicYear?: AcademicYearDto;
  academicYearId?: number;
  semester?: SemesterInfoDto | null;
  semesterId?: number;
  phase?: PhaseDto | null;
  phaseId?: number;
  priority?: 'academicYear' | 'semester' | 'phase';
  allowEmpty?: 'all' | 'semester' | 'phase' | 'none';
};

export type CorrectedSemesterFilterValues = {
  academicYear?: AcademicYearDto;
  semester?: SemesterInfoDto | null;
  phase?: PhaseDto | null;
};

const correctPhase = (
  state: {
    semesterFilter: SemesterSelectionFilter;
    selectedPhase: PhaseDto;
  },
  semester: SemesterInfoDto,
  phase: PhaseDto | undefined,
  allowEmpty?: 'all' | 'semester' | 'phase' | 'none',
) => {
  if (phase && semester?.id === phase.semesterId) {
    return phase;
  }

  if (allowEmpty) {
    return undefined;
  }

  return getPhaseForSemester(state, semester);
};

const correctSemesterForAcademicYear = (
  state: {
    semesterFilter: SemesterSelectionFilter;
    selectedSemester: SemesterInfoDto | undefined;
  },
  academicYear: AcademicYearDto,
  semester: SemesterInfoDto | undefined,
  phase: PhaseDto | undefined,
  allowEmpty: 'all' | 'semester' | 'phase' | 'none',
) => {
  if (semester && academicYear.id === semester.academicYearId) {
    return semester;
  }

  if (phase && academicYear.id === phase.academicYearId) {
    return getSemester(state, phase.semesterId);
  }

  if (allowEmpty === 'semester' || allowEmpty === 'all') {
    return undefined;
  }

  return getSemesterForAcademicYear(state, academicYear.id);
};

export const correctFilter = (
  state: {
    semesterFilter: SemesterSelectionFilter;
    selectedSemester: SemesterInfoDto;
    selectedPhase: PhaseDto;
    selectedAcademicYear: AcademicYearDto;
  },
  filter: SemesterFilterValues,
): SemesterFilterValues => {
  let phase = (filter.phase ??= filter.phaseId ? getPhase(state, filter.phaseId) : undefined);
  let semester = (filter.semester ??= filter.semesterId ? getSemester(state, filter.semesterId) : undefined);
  let academicYear = (filter.academicYear ??= filter.academicYearId
    ? getAcademicYear(state, filter.academicYearId)
    : undefined);

  if (filter.priority && filter.priority !== 'academicYear') {
    if (filter.priority === 'phase') {
      if (phase) {
        return correctFilter(state, { phase, allowEmpty: filter.allowEmpty });
      }
      if (semester) {
        return correctFilter(state, { semester, allowEmpty: filter.allowEmpty });
      }
      return correctFilter(state, { academicYear, allowEmpty: filter.allowEmpty });
    }

    if (filter.priority === 'semester') {
      if (semester) {
        return correctFilter(state, { semester, phase, allowEmpty: filter.allowEmpty });
      }
      return correctFilter(state, { academicYear, semester, phase, allowEmpty: filter.allowEmpty });
    }
  }

  if (academicYear) {
    semester = correctSemesterForAcademicYear(state, academicYear, semester, phase, filter.allowEmpty);
    phase = correctPhase(state, semester, phase, filter.allowEmpty);
    return { academicYear, semester, phase };
  }

  if (semester) {
    if (!academicYear) {
      academicYear = getAcademicYear(state, semester.academicYearId);
    }

    phase = correctPhase(state, semester, phase, filter.allowEmpty);
    return { academicYear, semester, phase };
  }

  if (phase) {
    return {
      academicYear: getAcademicYear(state, phase.academicYearId),
      semester: getSemester(state, phase.semesterId),
      phase,
    };
  }

  if (filter.allowEmpty === 'all') {
    return;
  }

  academicYear = state.selectedAcademicYear ?? getAcademicYear(state, undefined);
  semester = correctSemesterForAcademicYear(state, academicYear, undefined, undefined, filter.allowEmpty);
  phase = correctPhase(state, semester, undefined, filter.allowEmpty);
  return { academicYear, semester, phase };
};

export const matchSemesterFilter =
  (filter: SemesterFilterValues) => (dispatch: AppDispatch, getState: () => RootState) => {
    const state = getState().semesterSelection;
    const correctedFilter = correctFilter(state, filter);
    const change =
      state.selectedAcademicYear?.id === correctedFilter.academicYear?.id
        ? state.selectedSemester?.id === correctedFilter.semester?.id
          ? state.selectedPhase?.id === correctedFilter.phase?.id
            ? 'none'
            : 'phase'
          : 'semester'
        : 'academicYear';

    return { ...correctedFilter, change };
  };

export const adjustSemesterFilter =
  (filter: SemesterFilterValues, ignore: 'academicYear' | 'semester' | 'phase' | 'none') => (dispatch: AppDispatch) => {
    const result = dispatch(matchSemesterFilter(filter));
    if (result.change === 'none') {
      return result;
    }

    if (result.change === 'phase' && ['academicYear', 'semester', 'phase'].some((v) => v === ignore)) {
      return result;
    }

    if (result.change === 'semester' && ['academicYear', 'semester'].some((v) => v === ignore)) {
      return result;
    }

    if (result.change === 'academicYear' && 'academicYear' === ignore) {
      return result;
    }

    dispatch(semesterSelectionSlice.actions.setSemesterFilter(filter));
    return result;
  };

// Thunks
export const getSemesterFilterAsync = createAppThunk(
  'semesterFilter/GET_SEMESTER_FILTER',
  async (_, deps: { semesterSelectionClient: ISemesterSelectionClient }) => {
    return await deps.semesterSelectionClient.getSemesterSelectionFilter();
  },
  (r) => ({ semesterSelectionClient: r.resolve<ISemesterSelectionClient>('ISemesterSelectionClient') }),
);

const initialState = {
  loading: false,
  isLoaded: false,
  semesterFilter: null as SemesterSelectionFilter,
  errorMessage: null as string | null,
  selectedAcademicYear: null as AcademicYearDto,
  selectedSemester: null as SemesterInfoDto,
  selectedPhase: null as PhaseDto,
  openSemesters: [] as SemesterInfoDto[],
};

export type SemesterSelectionState = Readonly<typeof initialState>;

const semesterSelectionSlice = createSlice({
  name: 'semesterFilter',
  initialState,
  reducers: {
    setSemesterFilter(state, action: PayloadAction<SemesterFilterValues>) {
      const filter = correctFilter(state, action.payload);
      state.selectedAcademicYear = filter.academicYear;
      state.selectedSemester = filter.semester;
      state.selectedPhase = filter.phase;
    },
  },
  extraReducers(builder) {
    builder
      .addCase(getSemesterFilterAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(getSemesterFilterAsync.fulfilled, (state, action: PayloadAction<SemesterSelectionFilter>) => {
        state.semesterFilter = action.payload;
        const filter = correctFilter(state, {
          academicYear: state.selectedAcademicYear,
          semester: state.selectedSemester,
          phase: state.selectedPhase,
        });
        state.selectedAcademicYear = filter.academicYear;
        state.selectedSemester = filter.semester;
        state.selectedPhase = filter.phase;
        state.openSemesters = getOpenSemesters(action.payload);
        state.isLoaded = true;
        state.loading = false;
      })
      .addCase(getSemesterFilterAsync.rejected, (state) => {
        state.loading = false;
        state.isLoaded = true;
        state.errorMessage = 'failed to load semester filters';
      });
  },
});

export const semesterSelectionReducer = semesterSelectionSlice.reducer;
export const semesterSelectionActions = semesterSelectionSlice.actions;
